import {DatePipe} from '@angular/common';
import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {BarController, BarElement, CategoryScale, Decimation, Filler, Legend, Title, Tooltip} from 'chart.js';
import Chart from 'chart.js/auto';

@Component({
    selector: 'app-bar-chart',
    templateUrl: './bar-chart.component.html',
    styleUrl: './bar-chart.component.scss',
    standalone: false
})
export class BarChartComponent implements OnChanges {
  @Input() data: any;
  @Input() isBarChartLoder: any;
  @Input() isStacked: boolean = false;
  @Input() customColors: string[] = ['#4CAF50', '#F44336'];
  @Input() chartTitle: string = 'Storage Consumption';
  @Input() isMaterialChart: boolean = false;
  @Input() legendPosition: any = 'bottom';
  @Input() legendAlign: any = 'center';
  @Input() usePointStyle: boolean = false;
  @Input() legendBoxWidth: number = 12;
  @Input() legendFontSize: number = 14;

  constructor(private readonly datePipe: DatePipe) {
    Chart.register(BarElement, BarController, CategoryScale, Decimation, Filler, Legend, Title, Tooltip);
  }
  public chart: Chart;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.data) {
      const hasData = this.isStacked ? (this.data.length > 0) : this.data.some((x: any) => x.FileSize > 0);
      if (hasData) {
        this.createBarChart(this.data);
      } else {
        let chartExists = Chart.getChart("canvas");
        if (chartExists) {
          chartExists.destroy();
        }
        this.data = [];
      }
    }
  }

  getDataSet(data: any): any {
    let labels: any = [];
    let datasets: any = [];
    if (this.isStacked && data.length > 0 && data[0].datasets) {
      labels = data[0].labels;
      data[0].datasets.forEach((ds: any, index: number) => {
        datasets.push({
          type: 'bar',
          label: ds.label,
          data: ds.data,
          backgroundColor: this.customColors[index] || this.getRandomColor(),
          stack: 'stack1'
        });
      });
    } else {
      let storageValues: any = [];
      data.forEach((x: any) => {
        labels.push(x.label)
        storageValues.push(x.FileSize);
      });
      datasets.push({
        type: 'bar',
        label: 'Storage',
        data: storageValues,
        fill: false,
        borderColor: this.getRandomColor(),
        backgroundColor: this.getRandomColor()
      });
    }

    let chartExists = Chart.getChart("canvas");
    if (chartExists) {
      chartExists.destroy();
    }
    return {
      labels: labels,
      datasets: datasets
    };
  }

  createBarChart(data: any) {
    let dataSet = this.getDataSet(data);
    let totalStorageFormatted = '';
    if (!this.isStacked) {
      let totalStorage = data.reduce((sum: number, item: any) => sum + item.FileSize, 0);
      totalStorageFormatted = this.converStorage(totalStorage).value;
    }

    this.chart = new Chart("canvas", {
      type: "bar",
      data: dataSet,
      options: {
        responsive: true,
        plugins: {
          datalabels: {
            display: false
          },
          legend: {
            position: this.legendPosition,
            align: this.legendAlign,
            labels: {
              usePointStyle: this.usePointStyle,
              boxWidth: this.legendBoxWidth,
              font: {
                size: this.legendFontSize,
              },
              generateLabels: (chart) => {
                let labels: any = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                if (labels.length > 0 && !this.isStacked) {
                  labels[0].text += ` - Total: ${totalStorageFormatted}`;
                }
                return labels;
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (this.isStacked) return context.dataset.label + ': ' + context.parsed.y;
                return this.labelValues(context.parsed.y, context.dataset.label);
              }
            }
          }
        },
        scales: {
          x: {
            stacked: this.isStacked,
            grid: {
              display: false
            }
          },
          y: {
            stacked: this.isStacked,
            beginAtZero: true,
            ticks: {
              callback: (value: any, index, ticks) => {
                if (this.isStacked) {
                  if (value >= 1000) {
                    return (value / 1000) + 'K';
                  }
                  return value;
                }
                let modifiedSize: any = this.converStorage(value);
                let filteredArray = ticks.filter((tick: any) => { return tick.label === modifiedSize.value });
                if (filteredArray.length && modifiedSize !== '') {
                  modifiedSize.value = modifiedSize.orgValue.toFixed(1) + modifiedSize.type;
                }
                return modifiedSize.value;
              }
            }
          },
        }
      }
    });
  }

  converStorage(size: any) {
    if (size) {
      if (size < 1024) {
        return {
          value: '0.00 B',
          orgValue: 0,
          type: 'B'
        };
      } else if (size < 1024 * 1024) {
        return {
          value: (size / 1024).toFixed(2) + ' KB',
          orgValue: size / 1024,
          type: 'KB'
        };
      } else if (size < 1024 * 1024 * 1024) {
        return {
          value: (size / (1024 * 1024)).toFixed(2) + ' MB',
          orgValue: size / (1024 * 1024),
          type: 'MB'
        };
      } else {
        return {
          value: (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB',
          orgValue: size / (1024 * 1024 * 1024),
          type: 'GB'
        };
      }
    }
    return {
      value: '0.00 B',
      orgValue: 0,
      type: 'B'
    };
  }

  labelValues(size: any, label: any){
    if (size) {
      if (size < 1024) {
        return label + ': ' + 0 + ' B';
      } else if (size < 1024 * 1024) {
        return label + ': ' + +(size / 1024).toFixed(2) + ' KB';
      } else if (size < 1024 * 1024 * 1024) {
        return label + ': ' + +(size / (1024 * 1024)).toFixed(2) + ' MB';
      } else {
        return label + ': ' + +(size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
      }
    } else {
      return label + ': ' + '0 MB';
    }
  }

  getRandomColor() {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const random = array[0] / 2 ** 32;
    return '#' + Math.floor(random * 16777215).toString(16);
  }
}
